import { useParams, Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, MapPin, Star } from "lucide-react";
import {
    LANDING_PAGE_MAP,
    LANDING_PAGES,
    LANDING_PRODUCTS,
} from "../data/landing";
import { COMPANY, SERVICE_AREAS, SERVICE_IMAGES, TESTIMONIALS } from "../data/site";
import { useReveal } from "../hooks/use-reveal";
import { useSeo } from "../hooks/use-seo";
import {
    serviceJsonLd,
    aggregateRatingJsonLd,
    reviewListJsonLd,
    breadcrumbJsonLd,
    SITE_BASE_URL,
} from "../lib/jsonld";
import { ContactForm } from "../components/sections/ContactForm";
import NotFound from "./NotFound";

export default function Landing() {
    useReveal();
    const { slug } = useParams();
    const page = LANDING_PAGE_MAP[slug];

    const area = page?.area;
    const product = page?.product;
    const localTestimonials = area
        ? TESTIMONIALS.filter(
              (t) =>
                  t.location?.toLowerCase().includes(area.city.toLowerCase()) ||
                  t.location?.toLowerCase().includes(area.state.toLowerCase())
          ).slice(0, 3)
        : [];
    const showTestimonials =
        localTestimonials.length > 0 ? localTestimonials : TESTIMONIALS.slice(0, 3);

    useSeo({
        title: page?.title,
        description: page?.description,
        canonical: page ? `${SITE_BASE_URL}/${page.slug}` : undefined,
        jsonLd: page
            ? [
                  serviceJsonLd({
                      name: `${product.label} in ${area.city}, ${area.state}`,
                      description: product.intro,
                      url: `${SITE_BASE_URL}/${page.slug}`,
                      areaName: `${area.city}, ${area.state}`,
                  }),
                  aggregateRatingJsonLd(),
                  ...reviewListJsonLd(showTestimonials),
                  breadcrumbJsonLd([
                      { name: "Home", url: "/" },
                      { name: "Services", url: "/services" },
                      {
                          name: `${product.label} in ${area.city}`,
                          url: `/${page.slug}`,
                      },
                  ]),
              ]
            : undefined,
    });

    if (!page) return <NotFound />;

    const photo = SERVICE_IMAGES[product.serviceId];

    const otherProducts = LANDING_PRODUCTS.filter(
        (p) => p.keyword !== product.keyword
    ).slice(0, 6);
    const nearbyAreas = SERVICE_AREAS.filter((a) => a.slug !== area.slug).slice(0, 9);

    return (
        <main data-testid={`landing-${page.slug}`} className="pt-24">
            {/* Hero */}
            <section className="relative py-24 md:py-32 bg-ink text-bone overflow-hidden">
                {photo && (
                    <img
                        src={photo}
                        alt={product.label}
                        className="absolute inset-0 w-full h-full object-cover opacity-35"
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/30" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                    <Link
                        to="/services"
                        className="inline-flex items-center gap-2 text-sm text-bone/70 hover:text-ember mb-8 reveal"
                    >
                        <MapPin size={14} /> {product.label} · {area.city}, {area.state}
                    </Link>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.98] max-w-4xl reveal">
                        {product.label} in <span className="text-ember">{area.city}, {area.state}</span>
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-bone/85 max-w-3xl leading-relaxed reveal">
                        {product.intro} Serving {area.city} ({area.zip}) and the surrounding
                        Chicagoland corridor with up to{" "}
                        <strong className="text-ember">{COMPANY.maxRebate}</strong> in NICOR &amp;
                        ComEd rebates filed for you.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal">
                        <Link
                            to="/contact"
                            className="btn-lift inline-flex items-center justify-center gap-2 bg-copper text-bone font-semibold py-4 px-8 hover:bg-copper-hover"
                        >
                            Free quote in {area.city} <ArrowRight size={18} />
                        </Link>
                        <a
                            href={COMPANY.phoneHref}
                            className="btn-lift inline-flex items-center justify-center gap-2 border-2 border-bone text-bone font-semibold py-4 px-8 hover:bg-bone hover:text-ink"
                        >
                            <Phone size={16} /> {COMPANY.phone}
                        </a>
                    </div>
                </div>
            </section>

            {/* Why this product */}
            <section className="py-20 md:py-28 bg-bone">
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5 reveal">
                        Why {product.shortLabel}
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-10 reveal">
                        What {product.label.toLowerCase()} actually does for a {area.city} home.
                    </h2>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-5 reveal">
                        {product.why.map((w) => (
                            <li
                                key={w}
                                className="flex items-start gap-3 border border-line bg-white p-5"
                            >
                                <CheckCircle2
                                    size={20}
                                    strokeWidth={1.5}
                                    className="text-copper shrink-0 mt-0.5"
                                />
                                <span className="text-ink/90 leading-relaxed">{w}</span>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-10 border-l-4 border-copper pl-6 reveal">
                        <div className="text-xs uppercase tracking-[0.2em] font-bold text-copper mb-2">
                            When to use it
                        </div>
                        <p className="text-lg text-ink/85 leading-relaxed">{product.whenToUse}</p>
                    </div>
                </div>
            </section>

            {/* Why HAZ in this city */}
            <section className="py-20 md:py-28 bg-moss text-bone relative overflow-hidden">
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 relative grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7 reveal">
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5">
                            Why HAZ in {area.city}
                        </div>
                        <h2 className="font-heading text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight">
                            BPI certified. NICOR-approved. Real {area.city} crew.
                        </h2>
                        <p className="mt-6 text-bone/85 leading-relaxed text-lg">
                            Most of the housing stock in {area.city}, {area.state} was built before
                            modern energy codes — meaning attics commonly sit at R-19 to R-30 when
                            current code calls for R-49 to R-60, and air sealing was essentially
                            nonexistent. {product.label} is one of the highest-impact upgrades you
                            can make on these homes.
                        </p>
                    </div>
                    <ul className="lg:col-span-5 space-y-4 reveal">
                        {[
                            "BPI-certified owner-operator on every quote",
                            `Up to ${COMPANY.maxRebate} in NICOR & ComEd rebates filed for you`,
                            "Thermal imaging + blower door verification",
                            "Same crew, same standard — Jamie, Jose, Denni, Juan",
                            `${TESTIMONIALS.length}+ five-star Google reviews across Chicagoland`,
                        ].map((p) => (
                            <li key={p} className="flex items-start gap-3 text-bone/90">
                                <CheckCircle2
                                    size={20}
                                    strokeWidth={1.5}
                                    className="text-ember shrink-0 mt-1"
                                />
                                <span>{p}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Local reviews */}
            <section className="py-20 md:py-28 bg-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5 reveal">
                        Reviews
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-10 reveal">
                        What homeowners near {area.city} say.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {showTestimonials.map((t, i) => (
                            <article
                                key={i}
                                className="bg-white border border-line p-7 reveal"
                            >
                                <div className="flex mb-4">
                                    {Array.from({ length: t.rating }).map((_, k) => (
                                        <Star
                                            key={k}
                                            size={14}
                                            className="fill-ember text-ember"
                                        />
                                    ))}
                                </div>
                                <p className="text-sm leading-relaxed">{t.text}</p>
                                <div className="mt-5 pt-3 border-t border-line">
                                    <div className="font-heading font-bold text-sm">{t.name}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted_ink">
                                        {t.location}
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Cross-link other products in same city */}
            <section className="py-20 bg-ink text-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5 reveal">
                        Other services in {area.city}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-bone/10 border border-bone/15">
                        {otherProducts.map((p) => {
                            const stateLower = area.state.toLowerCase();
                            const targetSlug = `${p.keyword}-${area.slug}${
                                area.slug.endsWith(`-${stateLower}`) ? "" : `-${stateLower}`
                            }`;
                            return (
                                <Link
                                    key={p.keyword}
                                    to={`/${targetSlug}`}
                                    className="bg-ink p-6 hover:bg-moss group transition-colors"
                                >
                                    <h3 className="font-heading text-lg font-bold mb-1 group-hover:text-ember transition-colors">
                                        {p.label} in {area.city}
                                    </h3>
                                    <p className="text-sm text-bone/70 leading-relaxed line-clamp-2">
                                        {p.intro}
                                    </p>
                                </Link>
                            );
                        })}
                    </div>

                    <div className="mt-12 reveal">
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-3">
                            {product.shortLabel} in nearby cities
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {nearbyAreas.map((a) => {
                                const stateLower = a.state.toLowerCase();
                                const targetSlug = `${product.keyword}-${a.slug}${
                                    a.slug.endsWith(`-${stateLower}`) ? "" : `-${stateLower}`
                                }`;
                                return (
                                    <Link
                                        key={a.slug}
                                        to={`/${targetSlug}`}
                                        className="text-xs uppercase tracking-wider font-bold border border-bone/20 px-4 py-2 hover:bg-bone hover:text-ink transition-colors"
                                    >
                                        {a.city}, {a.state}
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            <ContactForm />
        </main>
    );
}

export { LANDING_PAGES };
